import SoftBox from "components/SoftBox";
import { useNavigate } from "react-router-dom";
import { useSlugConnector } from "hooks/useSlugConnector";
import Icon from "@mui/material/Icon";
import SoftButton from "components/SoftButton";
import { useContext, useEffect, useState } from "react";
import { PermissionsContext } from "context/AppProvider/PermissionsContext";
import { useAuth0 } from "@auth0/auth0-react";
import { PERMISSIONS } from "constants/portal";
import { allowAction } from "helpers/common";
import { useDispatch } from "react-redux";
import { deleteComponent } from "api/ComponentsApi";
import DeleteComponentConfirmationDialog from "layouts/pages/connector/components/DeleteComponentConfirmationDialog";
import { TrashComponentDialog } from "layouts/pages/connector/components/TrashComponentDialog";
import { updateUserComponentGroup } from "redux/actions/flowDesigner/componentGroupsActionCreator";
import { toast } from "react-toastify";
import { updateComponent } from "redux/actions/flowDesigner/cxfComponentsActionCreator";
import { useSelector } from "react-redux";
import { RecoverComponentDialog } from "layouts/pages/connector/components/RecoverComponentDialog";
import { RegisterComponentDialog } from "layouts/Flow Designer/components/RegisterComponentDialog";
import { useConnectorPermissions } from "hooks/useConnectorPermissions";

export const ConnectorOptions = ({ connector }) => {
  const navigate = useNavigate();
  const { handleFetchOwnedConnectors } = useSlugConnector(false);
  const { userPermissions } = useContext(PermissionsContext);
  const { user } = useAuth0();
  const dispatch = useDispatch();
  const [openTrashComponentDialog, setOpenTrashComponentDialog] = useState(false);
  const [openDeleteComponentConfirmationDialog, setOpenDeleteComponentConfirmationDialog] =
    useState(false);
  const { userComponentGroups } = useSelector((store) => store?.designer?.componentGroups);
  const [openRecoverComponentDialog, setOpenRecoverComponentDialog] = useState(false);
  const [actionType, setActionType] = useState("");
  const { savingComponentFlagSuccess } = useSelector((store) => store?.designer?.cxfComponents);
  const [openViewConnectorPropertiesDialog, setOpenViewConnectorPropertiesDialog] = useState(false);
  const { canEdit, canDelete } = useConnectorPermissions(connector);

  const handleDeleteComponent = async () => {
    let deleteComponentResponse;

    try {
      deleteComponentResponse = await deleteComponent(user?.stripeId, connector?.id);
    } catch {
      toast.error("Failed to delete connector");
      return false;
    }

    const foundComponentGroup = userComponentGroups?.find((group) =>
      group?.component_groups?.find(
        (groupComponent) => groupComponent.component_id == connector?.id
      )
    );

    if (foundComponentGroup) {
      const { components, created_date_time, id, ...restFoundComponentGroup } = foundComponentGroup;
      restFoundComponentGroup.component_groups = restFoundComponentGroup.component_groups.filter(
        (groupComponent) => groupComponent.component_id !== connector.id
      );

      dispatch(updateUserComponentGroup(user?.stripeId, id, restFoundComponentGroup));
    }

    if (deleteComponentResponse?.isSuccessful) {
      toast.success("Delete connector successful", {});
      handleFetchOwnedConnectors();

      setTimeout(() => {
        navigate("/workspace/connectors");
      }, 200);
    } else {
      toast.error(deleteComponentResponse?.message);
      return false;
    }

    return true;
  };

  const isAllowedToDelete = () => {
    if (
      allowAction(user?.cxfUserId, undefined, userPermissions, PERMISSIONS.DELETE_ANY_CONNECTORS) ||
      allowAction(user?.cxfUserId, undefined, userPermissions, PERMISSIONS.DELETE_CONNECTORS)
    ) {
      return true;
    }

    return false;
  };

  const handleCloseTrashComponentDialog = () => {
    setOpenTrashComponentDialog(false);
  };

  const handleTrashComponent = async () => {
    const input = {
      status: "deleted"
    };
    dispatch(updateComponent(user, connector.id, input));
    setActionType("trash");

    return true;
  };

  const handleCloseDeleteComponentDialog = () => {
    setOpenDeleteComponentConfirmationDialog(false);
  };

  const handleCloseRecoverComponentDialog = () => {
    setOpenRecoverComponentDialog(false);
  };

  const handleRecoverComponent = async () => {
    const input = {
      status: "active"
    };
    dispatch(updateComponent(user, connector.id, input));
    setActionType("recover");
  };

  const handleOpenDeleteComponentDialog = () => {
    if (!isAllowedToDelete()) return;

    setOpenDeleteComponentConfirmationDialog(true);
  };

  const handleDeletePermanentlyCallback = () => {
    handleCloseTrashComponentDialog();

    handleOpenDeleteComponentDialog();
  };

  const handleCloseConnectorPropertiesDialog = () => {
    setOpenViewConnectorPropertiesDialog(false);
  };

  const handleOpenConnectorPropertiesDialog = () => {
    setOpenViewConnectorPropertiesDialog(true);
  };

  const handleDeleteFromProperties = () => {
    if (!canDelete) return;
    handleCloseConnectorPropertiesDialog();
    if (connector?.status === "deleted") {
      handleOpenDeleteComponentDialog();
    } else {
      setOpenTrashComponentDialog(true);
    }
  };

  useEffect(() => {
    if (!savingComponentFlagSuccess || !actionType) return;

    if (actionType === "trash") {
      toast.success("Connector successfully trashed");
    } else if (actionType === "recover") {
      toast.success("Connector successfully recovered");
    }

    setActionType("");
  }, [savingComponentFlagSuccess, actionType]);

  return (
    <>
      <SoftBox display="flex" sx={{ marginTop: "auto" }}>
        <SoftButton
          sx={{
            margin: "0 20px 0 0",
            padding: "12px !important"
          }}
          variant="gradient"
          size="medium"
          color="primary"
          onClick={handleOpenConnectorPropertiesDialog}
        >
          <Icon sx={{ fontSize: "500px !important", marginRight: "8px" }}>edit</Icon>
          properties
        </SoftButton>
      </SoftBox>
      <DeleteComponentConfirmationDialog
        open={openDeleteComponentConfirmationDialog}
        onClose={handleCloseDeleteComponentDialog}
        onAccept={handleDeleteComponent}
        component={connector}
      />
      <TrashComponentDialog
        open={openTrashComponentDialog}
        onClose={handleCloseTrashComponentDialog}
        onAccept={handleTrashComponent}
        component={connector}
        deletePermanentlyCallback={handleDeletePermanentlyCallback}
      />
      <RecoverComponentDialog
        open={openRecoverComponentDialog}
        onClose={handleCloseRecoverComponentDialog}
        onAccept={handleRecoverComponent}
      />
      <RegisterComponentDialog
        component={connector}
        open={openViewConnectorPropertiesDialog}
        isReadOnly={!canEdit}
        canDelete={canDelete}
        onDelete={handleDeleteFromProperties}
        onClose={handleCloseConnectorPropertiesDialog}
      />
    </>
  );
};
